import { useEffect, useState } from "react";
import { FiX } from "react-icons/fi";
import { GST_STATES } from '../../utils/gstStates';
import AgreementDefaults from './AgreementDefaults';

const emptyForm = {
  agreementType: '',
  brandAgreementTypes: [],
  displayName: "",
  legalName: "",
  code: "",
  email: "",
  phone: "",
  additionalPhones: [],
  gstin: "",
  pan: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  postalCode: "",
  country: "IN",
  isActive: true,
};

export default function SupplierModal({
  open,
  onClose,
  onSave,
  initialData = null,
}) {
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!open) return;

    setForm(
      initialData
        ? {
            displayName: initialData.displayName || "",
            agreementType: initialData.agreementType || '',
            brandAgreementTypes: initialData.brandAgreementTypes || [],
            legalName: initialData.legalName || "",
            code: initialData.code || "",
            email: initialData.email || "",
            phone: initialData.phone || "",
            additionalPhones: initialData.additionalPhones || [],
            gstin: initialData.gstin || "",
            pan: initialData.pan || "",
            addressLine1: initialData.addressLine1 || "",
            addressLine2: initialData.addressLine2 || "",
            city: initialData.city || "",
            state: initialData.state || "",
            postalCode: initialData.postalCode || "",
            country: initialData.country || "IN",
            isActive: initialData.isActive ?? true,
          }
        : emptyForm,
    );
  }, [open, initialData]);

  if (!open) return null;

  const updateField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
      ...(field === 'gstin' && GST_STATES[value.slice(0, 2)] ? { state: GST_STATES[value.slice(0, 2)], country: 'IN' } : {}),
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    onSave({
      ...form,
      code: form.code.trim().toUpperCase(),
      displayName: form.displayName.trim(),
      legalName: form.legalName.trim() || undefined,
      email: form.email.trim() || undefined,
      phone: form.phone.trim() || undefined,
      additionalPhones: [...new Set(form.additionalPhones.map(value => value.trim()).filter(Boolean))],
      gstin: form.gstin.trim().toUpperCase() || undefined,
      pan: form.pan.trim().toUpperCase() || undefined,
      addressLine1: form.addressLine1.trim() || undefined,
      addressLine2: form.addressLine2.trim() || undefined,
      city: form.city.trim() || undefined,
      state: form.state.trim() || undefined,
      postalCode: form.postalCode.trim() || undefined,
      country: form.country.trim().toUpperCase() || "IN",
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-white shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-100 bg-white px-8 py-6">
          <div>
            <h2 className="text-2xl font-bold">
              {initialData ? "Edit Supplier" : "Add Supplier"}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Manage vendor information for purchases.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200"
          >
            <FiX />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-5 p-8 md:grid-cols-2">
            <AgreementDefaults value={form.agreementType} onChange={value => updateField('agreementType', value)} overrides={form.brandAgreementTypes} onOverridesChange={value => updateField('brandAgreementTypes', value)} />
            <div>
              <label className="mb-2 block text-sm font-medium">
                Display Name *
              </label>
              <input
                value={form.displayName}
                placeholder="ABC Jewellery"
                onChange={(event) => updateField("displayName", event.target.value)}
                className="h-12 w-full rounded-xl border border-gray-200 px-4"
                required
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">
                Supplier Code *
              </label>
              <input
                value={form.code}
                placeholder="ABC001"
                onChange={(event) => updateField("code", event.target.value.toUpperCase())}
                className="h-12 w-full rounded-xl border border-gray-200 px-4"
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="mb-2 block text-sm font-medium">
                Legal Name
              </label>
              <input
                value={form.legalName}
                placeholder="ABC Jewellery Private Limited"
                onChange={(event) => updateField("legalName", event.target.value)}
                className="h-12 w-full rounded-xl border border-gray-200 px-4"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">Phone</label>
              <input
                value={form.phone}
                placeholder="+91 9876543210"
                onChange={(event) => updateField("phone", event.target.value)}
                className="h-12 w-full rounded-xl border border-gray-200 px-4"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">Additional phone numbers</label>
              <div className="space-y-2">
                {form.additionalPhones.map((phone, index) => <div className="flex gap-2" key={index}><input type="tel" maxLength={20} aria-label={`Additional phone ${index + 1}`} value={phone} onChange={event => updateField('additionalPhones', form.additionalPhones.map((value, i) => i === index ? event.target.value : value))} placeholder="Mobile / landline number" className="h-12 min-w-0 flex-1 rounded-xl border border-gray-200 px-4"/><button type="button" aria-label={`Remove phone ${index + 1}`} onClick={() => updateField('additionalPhones', form.additionalPhones.filter((_, i) => i !== index))} className="px-3 text-red-600"><FiX/></button></div>)}
                <button type="button" disabled={form.additionalPhones.length >= 10} onClick={() => updateField('additionalPhones', [...form.additionalPhones, ''])} className="text-sm font-semibold text-primary-800 disabled:opacity-50">+ Add phone number</button>
              </div>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium">Email</label>
              <input
                type="email"
                value={form.email}
                placeholder="purchases@abc.com"
                onChange={(event) => updateField("email", event.target.value)}
                className="h-12 w-full rounded-xl border border-gray-200 px-4"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">GSTIN</label>
              <input
                value={form.gstin}
                maxLength={15}
                pattern="[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]"
                title="Enter a valid 15-character GSTIN"
                placeholder="27ABCDE1234F1Z5"
                onChange={(event) => updateField("gstin", event.target.value.toUpperCase())}
                className="h-12 w-full rounded-xl border border-gray-200 px-4"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">PAN</label>
              <input
                value={form.pan}
                placeholder="ABCDE1234F"
                onChange={(event) => updateField("pan", event.target.value.toUpperCase())}
                className="h-12 w-full rounded-xl border border-gray-200 px-4"
              />
            </div>

            <div className="md:col-span-2">
              <label className="mb-2 block text-sm font-medium">Address Line 1</label>
              <input
                value={form.addressLine1}
                onChange={(event) => updateField("addressLine1", event.target.value)}
                className="h-12 w-full rounded-xl border border-gray-200 px-4"
              />
            </div>

            <div className="md:col-span-2">
              <label className="mb-2 block text-sm font-medium">Address Line 2</label>
              <input
                value={form.addressLine2}
                onChange={(event) => updateField("addressLine2", event.target.value)}
                className="h-12 w-full rounded-xl border border-gray-200 px-4"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">City</label>
              <input
                value={form.city}
                onChange={(event) => updateField("city", event.target.value)}
                className="h-12 w-full rounded-xl border border-gray-200 px-4"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">State</label>
              <input
                value={form.state}
                readOnly={Boolean(GST_STATES[form.gstin.slice(0, 2)])}
                onChange={(event) => updateField("state", event.target.value)}
                className="h-12 w-full rounded-xl border border-gray-200 px-4"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">Postal Code</label>
              <input
                value={form.postalCode}
                onChange={(event) => updateField("postalCode", event.target.value)}
                className="h-12 w-full rounded-xl border border-gray-200 px-4"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">Status</label>
              <select
                value={form.isActive ? "active" : "inactive"}
                onChange={(event) => updateField("isActive", event.target.value === "active")}
                className="h-12 w-full rounded-xl border border-gray-200 px-4"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="sticky bottom-0 flex justify-end gap-3 border-t border-gray-100 bg-white px-8 py-5">
            <button type="button" onClick={onClose} className="h-11 rounded-xl border border-gray-200 px-6">
              Cancel
            </button>
            <button type="submit" className="h-11 rounded-xl bg-primary-800 px-6 text-white hover:bg-primary-900">
              {initialData ? "Save Changes" : "Create Supplier"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
